/**
 * Image optimization utilities for better performance
 */

interface ImageConfig {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  format?: 'webp' | 'jpeg' | 'png';
}

interface LazyImageOptions {
  threshold?: number;
  rootMargin?: string;
  placeholder?: string;
}

/**
 * Compress and resize image on the client side
 */
export const compressImage = (
  file: File,
  config: ImageConfig = {}
): Promise<{ file: File; size: number; dimensions: { width: number; height: number } }> => {
  return new Promise((resolve, reject) => {
    const {
      quality = 0.8,
      maxWidth = 1920,
      maxHeight = 1080,
      format = 'webp'
    } = config;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx?.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }

          const compressedFile = new File([blob], file.name, {
            type: `image/${format}`,
            lastModified: Date.now(),
          });

          resolve({
            file: compressedFile,
            size: blob.size,
            dimensions: { width, height },
          });
        },
        `image/${format}`,
        quality
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Create lazy loading observer for images
 */
export const createLazyImageObserver = (
  options: LazyImageOptions = {}
): IntersectionObserver => {
  const {
    threshold = 0.1,
    rootMargin = '50px 0px',
  } = options;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const img = entry.target as HTMLImageElement;
        const src = img.dataset.src;
        
        if (src) {
          // Load the actual image
          const actualImg = new Image();
          actualImg.onload = () => {
            img.src = src;
            img.classList.remove('lazy-loading');
            img.classList.add('lazy-loaded');
          };
          actualImg.onerror = () => {
            img.classList.add('lazy-error');
          };
          actualImg.src = src;
          
          // Stop observing this image
          observer.unobserve(img);
        }
      }
    });
  }, {
    threshold,
    rootMargin,
  });

  return observer;
};

// Global lazy image observer
let globalObserver: IntersectionObserver | null = null;

/**
 * Initialize global lazy loading for images
 */
export const initializeLazyLoading = (options?: LazyImageOptions): void => {
  if (typeof window === 'undefined') return;

  globalObserver = createLazyImageObserver(options);

  // Observe all images with data-src attribute
  const lazyImages = document.querySelectorAll('img[data-src]');
  lazyImages.forEach((img) => {
    globalObserver?.observe(img);
  });
};

/**
 * Add an image to lazy loading observation
 */
export const observeLazyImage = (img: HTMLImageElement): void => {
  if (globalObserver) {
    globalObserver.observe(img);
  }
};

/**
 * Generate responsive image srcSet
 */
export const generateSrcSet = (
  baseUrl: string,
  sizes: number[] = [320, 640, 960, 1280, 1920]
): string => {
  return sizes.map(size => `${baseUrl}?w=${size} ${size}w`).join(', ');
};

/**
 * Get optimized image URL (for external services like Unsplash)
 */
export const getOptimizedImageUrl = (
  originalUrl: string,
  width?: number,
  height?: number,
  quality?: number
): string => {
  try {
    const url = new URL(originalUrl);
    
    // Handle Unsplash URLs
    if (url.hostname.includes('unsplash.com')) {
      if (width) url.searchParams.set('w', width.toString());
      if (height) url.searchParams.set('h', height.toString());
      if (quality) url.searchParams.set('q', quality.toString());
      url.searchParams.set('auto', 'format');
      url.searchParams.set('fit', 'crop');
      return url.toString();
    }

    // Handle other image services or return original
    return originalUrl;
  } catch {
    return originalUrl;
  }
};

/**
 * Preload critical images
 */
export const preloadImage = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Preload multiple images
 */
export const preloadImages = (sources: string[]): Promise<void[]> => {
  return Promise.all(sources.map(preloadImage));
};

/**
 * Check if WebP is supported
 */
export const isWebPSupported = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const webP = new Image();
    webP.onload = webP.onerror = () => {
      resolve(webP.height === 2);
    };
    webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
  });
};

/**
 * Get image dimensions from file
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Create a placeholder for lazy-loaded images
 */
export const createImagePlaceholder = (
  width: number,
  height: number,
  backgroundColor = '#f3f4f6'
): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = width;
  canvas.height = height;
  
  if (ctx) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    
    // Add loading indicator
    ctx.fillStyle = '#9ca3af';
    ctx.font = '14px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('Loading...', width / 2, height / 2);
  }
  
  return canvas.toDataURL();
};

/**
 * Progressive image loading utility
 */
export class ProgressiveImage {
  private img: HTMLImageElement;
  private lowResSrc: string;
  private highResSrc: string;
  private onLoad?: () => void;
  private onError?: (error: Error) => void;

  constructor(
    img: HTMLImageElement,
    lowResSrc: string,
    highResSrc: string,
    callbacks: {
      onLoad?: () => void;
      onError?: (error: Error) => void;
    } = {}
  ) {
    this.img = img;
    this.lowResSrc = lowResSrc;
    this.highResSrc = highResSrc;
    this.onLoad = callbacks.onLoad;
    this.onError = callbacks.onError;
  }

  load(): void {
    // First load low-res image
    const lowResImg = new Image();
    lowResImg.onload = () => {
      this.img.src = this.lowResSrc;
      this.img.classList.add('progressive-low-res');
      this.loadHighRes();
    };
    lowResImg.onerror = () => {
      this.onError?.(new Error('Failed to load low-res image'));
    };
    lowResImg.src = this.lowResSrc;
  }

  private loadHighRes(): void {
    const highResImg = new Image();
    highResImg.onload = () => {
      this.img.src = this.highResSrc;
      this.img.classList.remove('progressive-low-res');
      this.img.classList.add('progressive-high-res');
      this.onLoad?.();
    };
    highResImg.onerror = () => {
      this.onError?.(new Error('Failed to load high-res image'));
    };
    highResImg.src = this.highResSrc;
  }
} 